/* eslint-disable react/jsx-props-no-spreading */
import { Stack, Box, Typography, Button, IconButton, FormLabel, FormHelperText } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { FCNC } from '../FCNC';
import { CloudUpload, Help } from '@mui/icons-material';
import { useRef, useState } from 'react';
import { DropzoneBoxFile } from './DropzoneBoxFile';
import { Tooltip } from '../Tooltip';
import { generateDefaultDisplayText } from './DropzoneBox.utils';
import { useFieldsDisabled } from '../Form';

export interface IDropzoneBoxProps {
    maxNumberOfFiles: number;
    setAcceptedFiles: (files: File[]) => void;
    /** Seeds the file list so a previous selection stays visible when the consumer remounts (e.g. multi-step forms). */
    initialFiles?: File[];
    acceptedFileTypes?: string | string[];
    displayText?: string;
    helpText?: string;
    testId?: string;
    disableDownload?: boolean;
    /** Form label rendered above the box. */
    label?: string;
    /** Adds an asterisk to the label and the drop text. */
    required?: boolean;
    /** Turns the label and border red. */
    error?: boolean;
    /** Validation message rendered under the box. */
    errorText?: string;
    /** Shows the current files but blocks adding or removing them. */
    disabled?: boolean;
}

export const DropzoneBox: FCNC<IDropzoneBoxProps> = ({
    maxNumberOfFiles,
    setAcceptedFiles,
    initialFiles,
    displayText,
    helpText,
    testId,
    acceptedFileTypes,
    disableDownload = false,
    label,
    required = false,
    error = false,
    errorText,
    disabled,
}) => {
    if (maxNumberOfFiles < 1) {
        throw new Error('maxNumberOfFiles must be greater than 0');
    }

    const [currentFiles, setCurrentFiles] = useState<File[]>(initialFiles ?? []);
    const isDisabled = useFieldsDisabled(disabled);

    const { getRootProps, getInputProps } = useDropzone({
        maxFiles: maxNumberOfFiles,
        onDropAccepted: (files) => {
            const updatedFiles = [...currentFiles, ...files];
            if (updatedFiles.length > maxNumberOfFiles) {
                return;
            }

            setAcceptedFiles(updatedFiles);
            setCurrentFiles(updatedFiles);
        },
        accept: acceptedFileTypes,
        disabled: isDisabled,
    });
    const inputRef = useRef<HTMLInputElement>(null);

    const triggerFileSelection = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    const displayMessage = displayText ?? generateDefaultDisplayText(maxNumberOfFiles, acceptedFileTypes, required);

    const deleteFile = (fileToDelete: File) => {
        const updatedFiles = currentFiles.filter((file) => file !== fileToDelete);

        setAcceptedFiles(updatedFiles);
        setCurrentFiles(updatedFiles);
    };

    return (
        <Box data-testid={`dropzone-box-${testId}`} p={1} width="100%">
            {label && (
                <FormLabel required={required} error={error} sx={{ typography: 'body2' }}>
                    {label}
                </FormLabel>
            )}
            <Stack
                {...getRootProps()} // wrap entire stack with dropzone props
                direction="column"
                alignItems="center"
                justifyContent="center"
                gap={2}
                width="100%"
                sx={{
                    backgroundColor: 'background.default',
                    border: (t) => `dashed 1px ${error ? t.palette.error.main : t.palette.grey[300]}`,
                    borderRadius: 1,
                    mt: label ? 1 : 0,
                }}
                p={1}
            >
                <IconButton
                    aria-label="Upload file"
                    onClick={triggerFileSelection}
                    disabled={isDisabled}
                    sx={{ color: 'black' }}
                >
                    <CloudUpload />
                </IconButton>
                <input
                    {...getInputProps()}
                    ref={inputRef}
                    aria-label="File upload"
                    key={currentFiles.length} // This is to reset the input field after a file is selected
                    disabled={isDisabled || currentFiles.length === maxNumberOfFiles}
                />
                <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                    <Typography
                        variant="caption"
                        component="span"
                        data-testid={`attach-file-${testId}`}
                        textAlign={'center'}
                        sx={{ whiteSpace: 'pre-line' }}
                    >
                        {displayMessage}
                    </Typography>
                    {helpText && (
                        <Tooltip title={helpText}>
                            <Help color="primary" />
                        </Tooltip>
                    )}
                </Stack>
                <Button
                    variant="contained"
                    onClick={triggerFileSelection}
                    disabled={isDisabled || currentFiles.length === maxNumberOfFiles}
                >
                    Or Browse Files
                </Button>
            </Stack>
            {error && errorText && <FormHelperText error>{errorText}</FormHelperText>}
            <Box component="aside" width={'100%'} py={1}>
                <Stack direction="column" alignItems="center" justifyContent="center" width={'100%'} minHeight={'60px'}>
                    {currentFiles.map((file, index) => (
                        <DropzoneBoxFile
                            id={index}
                            file={file}
                            onDeleteFile={isDisabled ? undefined : deleteFile}
                            disableDownload={disableDownload}
                            disableFileStatus={isDisabled}
                        />
                    ))}
                </Stack>
            </Box>
        </Box>
    );
};
